import { useEffect, useRef, useState } from 'react';
import { navigationSections } from '../content';
import { MagneticCta } from '../components/MagneticCta';
import { siteConfig } from '../../../shared/config/site';
import { scrollToSection } from '../../../shared/lib/scroll';

/** Sections the header has to invert over. */
const DARK_SECTION_IDS = ['hero', 'companions'];

export function HeaderSection() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [onDark, setOnDark] = useState(true);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let frame = 0;

    /**
     * Ask each dark section where it is on screen rather than where it sits in
     * the document. ScrollTrigger wraps a pinned section in a positioned
     * spacer, which resets its `offsetTop` to 0 and makes any document-space
     * arithmetic report the companions rail as sitting at the top of the page.
     *
     * The probe line is the header's own middle, read from the header. A fixed
     * offset cannot work: the header hangs below the demo banner, and that
     * banner is 61px at desktop width but 105px on a phone, so any constant
     * tuned for one lands above the hero on the other and leaves a light header
     * sitting on a dark hero.
     */
    const measure = () => {
      frame = 0;
      const header = headerRef.current?.getBoundingClientRect();

      if (!header) {
        return;
      }

      const probe = header.top + header.height / 2;
      setOnDark(
        DARK_SECTION_IDS.some((id) => {
          const rect = document.getElementById(id)?.getBoundingClientRect();
          return Boolean(rect && rect.top <= probe && rect.bottom > probe);
        }),
      );
    };

    // Reading layout inside the scroll handler forces a reflow on every event;
    // one read per frame is enough for a two-state swap.
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(measure);
      }
    };

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // The banner sets the header's `top`, and it reflows when the webfont
    // lands and its copy rewraps. That moves the probe without a resize event.
    void document.fonts?.ready.then(schedule);

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  // An open menu covers the page, so Escape closes it and hands focus back to
  // the toggle that opened it.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      setMenuOpen(false);
      menuButtonRef.current?.focus();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  const handleNavClick = (sectionId: string) => {
    setMenuOpen(false);
    void scrollToSection(sectionId);
  };

  return (
    <header
      ref={headerRef}
      className={`fixed inset-x-0 top-[var(--demo-banner-height)] z-50 transition-colors duration-300 ${
        onDark
          ? 'border-b border-white/10 bg-stage/85 text-mist backdrop-blur-md'
          : 'border-b border-neutral-200/80 bg-surface/95 text-ink backdrop-blur-md'
      }`}
    >
      <div className="mx-auto flex max-w-[var(--container-max)] items-center justify-between px-[var(--section-padding-x)] py-3.5">
        <button
          type="button"
          onClick={() => void scrollToSection('hero')}
          className={`flex min-h-11 items-center font-display text-xl font-semibold tracking-tight sm:text-2xl ${
            onDark ? 'text-mist' : 'text-ink'
          }`}
          aria-label={`${siteConfig.shortName} home`}
        >
          {siteConfig.shortName}
        </button>

        <nav className="hidden items-center gap-7 xl:flex" aria-label="Primary">
          {navigationSections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => handleNavClick(section.id)}
              className={`flex min-h-11 items-center px-1 font-body text-sm transition-colors duration-200 ease-in-out ${
                onDark ? 'text-white/70 hover:text-mist' : 'text-neutral-600 hover:text-ink'
              }`}
            >
              {section.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <MagneticCta href={siteConfig.phoneHref} variant={onDark ? 'secondary' : 'primary'}>
            Call the clinic
          </MagneticCta>
        </div>

        <button
          type="button"
          className={`flex h-11 w-11 items-center justify-center rounded-md border xl:hidden ${
            onDark ? 'border-white/25 text-mist' : 'border-neutral-300 text-ink'
          }`}
          ref={menuButtonRef}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            {menuOpen ? (
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            ) : (
              <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </div>

      {menuOpen ? (
        <nav
          id="mobile-nav"
          className={`border-t px-[var(--section-padding-x)] py-6 xl:hidden ${
            onDark ? 'border-white/10 bg-stage text-mist' : 'border-neutral-200 bg-surface text-ink'
          }`}
          aria-label="Mobile"
        >
          <ul className="flex flex-col gap-4">
            {navigationSections.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => handleNavClick(section.id)}
                  className="min-h-11 font-body text-lg"
                >
                  {section.label}
                </button>
              </li>
            ))}
            <li className="pt-2">
              <MagneticCta href={siteConfig.phoneHref} variant="secondary" className="w-full">
                Call the clinic
              </MagneticCta>
            </li>
          </ul>
        </nav>
      ) : null}
    </header>
  );
}
